import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@homy/ui";
import { isNavLeafActive } from "@/components/layout/appNavConfig";

/**
 * Collapsed icon rail: one control per group, menu to the right (Flightdeck-style).
 * Hover-open only when `hoverOpen` (md + fine pointer); otherwise tap/click opens.
 */
export function CollapsedNavGroupDropdown({
  section,
  sectionIndex,
  pathname,
  onNavigate,
  hoverOpen,
}) {
  const { t } = useTranslation("common");
  const [menuOpen, setMenuOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (!hoverOpen) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setMenuOpen(true);
  };

  const handleMouseLeave = () => {
    if (!hoverOpen) return;
    timeoutRef.current = setTimeout(() => {
      setMenuOpen(false);
    }, 150); // 150ms buffer to cross the gap
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const FirstIcon = section.children[0]?.icon ?? ChevronRight;
  const groupHasActiveChild = section.children.some((l) =>
    isNavLeafActive(l, pathname),
  );

  return (
    <div
      className={cn(
        "w-full",
        sectionIndex > 0 && "my-1 border-t border-border/40 pt-1",
      )}
    >
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-11 h-11 md:w-11 md:min-h-11 mx-auto items-center justify-center rounded-xl px-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
              groupHasActiveChild
                ? "bg-accent text-accent-foreground shadow-sm"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
            title={section.label}
            aria-label={t("nav.openGroup", { group: section.label })}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <FirstIcon size={24} className="h-6 w-6 shrink-0" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={6}
          className="w-56 p-1"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {section.children.map((leaf) => {
            const Icon = leaf.icon;
            const active = isNavLeafActive(leaf, pathname);
            return (
              <DropdownMenuItem
                key={leaf.id}
                onSelect={() => {
                  onNavigate(leaf.path);
                  setMenuOpen(false);
                }}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 min-h-[44px] md:min-h-9 md:py-1.5",
                  active && "bg-accent text-accent-foreground font-medium",
                )}
              >
                <Icon size={18} className="shrink-0" aria-hidden />
                <span className="flex-1 text-left text-sm">{leaf.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
